import CustomTooltip from '../../../shared/CustomTooltip';
import React, { MouseEvent, SyntheticEvent, useRef, useState } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import {
    Alert,
    Box,
    Button,
    Card,
    Divider,
    List,
    ListItemButton,
    ListItemText,
    Popover,
    TextField,
    Typography,
} from '@mui/material';
import { ArrowDropDown, HelpOutline } from '@mui/icons-material';

const shortCodes = [
    {
        desc: 'Reservation reference id',
        value: 'ref',
    },
    {
        desc: "First name of a reservation's primary guest",
        value: 'guest first name',
    },
    {
        desc: "Last name of a reservation's primary guest",
        value: 'guest last name',
    },
    { desc: 'Name of the reserved experience', value: 'experience name' },
    { desc: 'Reservation check-in date', value: 'checkin date' },
    { desc: 'Reservation check-out date', value: 'checkout date' },
    { desc: 'Reservation check-in time', value: 'checkin time' },
    { desc: 'Reservation checkout time', value: 'checkout time' },
    { desc: 'Experience check-in details', value: 'checkin details' },
    { desc: 'Experience wifi details', value: 'wifi details' },
    { desc: 'Experience address', value: 'address' },
];

const MessageField = () => {
    const { control, getValues, setValue } = useFormContext();

    const inputRef = useRef<HTMLInputElement>(null);
    const [cursorPosition, setCursorPosition] = useState(0);

    const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);

    const handleClick = (event: MouseEvent<HTMLButtonElement>) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const handleShortcode = (shortCode: string) => {
        const currentValue = getValues('message') || '';
        const newValue =
            currentValue.slice(0, cursorPosition) +
            `{{${shortCode}}}` +
            currentValue.slice(cursorPosition);

        setValue('message', newValue);

        // Move cursor after inserted shortcode
        setTimeout(() => {
            inputRef.current?.focus();
            const newCursorPosition = cursorPosition + shortCode.length + 4;
            inputRef.current?.setSelectionRange(
                newCursorPosition,
                newCursorPosition,
            );
            setCursorPosition(newCursorPosition);
        }, 0);

        handleClose();
    };

    const open = Boolean(anchorEl);

    const handleSelect = (event: SyntheticEvent<HTMLDivElement>) => {
        if (!inputRef.current || inputRef.current.selectionStart === null)
            return;

        setCursorPosition(inputRef.current?.selectionStart);
    };

    return (
        <>
            <Card variant='outlined'>
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'flex-end',
                        alignItems: 'center',
                        gap: 1,
                        px: 1,
                    }}
                >
                    <Button
                        size='small'
                        endIcon={<ArrowDropDown />}
                        onClick={handleClick}
                    >
                        Shortcodes
                    </Button>
                    <Popover
                        open={open}
                        anchorEl={anchorEl}
                        onClose={handleClose}
                        anchorOrigin={{
                            vertical: 'bottom',
                            horizontal: 'left',
                        }}
                    >
                        <Box sx={{ p: 2 }}>
                            <Typography>
                                Select a variable to insert it into your
                                template
                            </Typography>
                        </Box>
                        <Divider />
                        <List>
                            {shortCodes.map((shortCode, index) => (
                                <ListItemButton
                                    key={index}
                                    onClick={() =>
                                        handleShortcode(shortCode.value)
                                    }
                                >
                                    <ListItemText
                                        // remove braces
                                        primary={shortCode.value}
                                        secondary={shortCode.desc}
                                        primaryTypographyProps={{
                                            fontWeight: 'bold',
                                        }}
                                    />
                                </ListItemButton>
                            ))}
                        </List>
                    </Popover>
                    <CustomTooltip title='Use shortcodes to enter relevant guest and booking information into your messages. Select a variable to insert the shortcode into your template.'>
                        <HelpOutline fontSize='small' />
                    </CustomTooltip>
                </Box>
                <Controller
                    name='message'
                    control={control}
                    render={({
                        field: { value, onChange },
                        fieldState: { error },
                    }) => (
                        <TextField
                            id='message__input'
                            inputRef={inputRef}
                            placeholder='Hi, {{guest first name}}...'
                            size='small'
                            fullWidth
                            value={value}
                            onSelect={handleSelect}
                            onChange={onChange}
                            multiline
                            rows={10}
                            error={!!error}
                            helperText={!!error && error.message}
                        />
                    )}
                />
            </Card>
            <Alert severity='info' sx={{ mt: 1 }}>
                Use shortcodes to insert guest and reservation information into
                your message
            </Alert>
        </>
    );
};

export default MessageField;
